import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';

import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_INTERACTION_TYPES
} from 'dpl/constants/analytics';
import Icon from 'dpl/common/components/Icon';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import { pushParams } from 'dpl/actions/query_params';
import { REACTIONABLE_ID, REACTIONABLE_TYPE } from 'dpl/constants/query_params';
import { SIGNUP_FORM_TYPES } from 'dpl/shared/constants/shared';
import {
  SIGNUP_REF_ACTION,
  SIGNUP_REF_ACTION_TYPES
} from 'dpl/auth/utils/constants';

import useUserReactionsData from '../utils/useUserReactionsData';
import { REACTIONABLE_TYPES, REACTION_TYPES } from '../utils/constants';

const likeButtonDefaultPropAnalytics = {};
/** @param {any} props */
export default function LikeButton({
  analytics = likeButtonDefaultPropAnalytics,
  numOfLikes = 0,
  postId,
  onLike = null
}) {
  const {
    isLoading,
    userHasReacted: isLikedPost,
    createUserReaction,
    removeUserReaction,
    userReactionsData
  } = useUserReactionsData(postId);

  const { isLoggedIn } = useCurrentUser();
  const dispatch = useDispatch();

  const [likes, setLikes] = useState(numOfLikes);
  const [isLiked, setIsLiked] = useState(isLikedPost);

  useEffect(() => {
    setIsLiked(isLikedPost);
  }, [isLikedPost]);

  useEffect(() => {
    setLikes(numOfLikes);
  }, [numOfLikes]);

  if (isLoading) {
    return <SkeletonBlock className="mr1" height="24px" width="24px" />;
  }

  const iconName = isLiked ? 'fetch-heart-full-breeder' : 'fetch-heart';

  async function updateUserReaction() {
    setIsLiked(!isLiked);

    if (!isLiked) {
      setLikes(likes + 1);

      sendRudderstackEvent(ANALYTICS_EVENTS.SOCIAL_POST_INTERACTION_BY_USER, {
        interaction_type: ANALYTICS_INTERACTION_TYPES.LIKE,
        breeder_post_id: postId,
        ...analytics
      });

      await createUserReaction({
        body: {
          reactionable_type: REACTIONABLE_TYPES.BREEDER_POST,
          reactionable_id: postId,
          reaction_type: REACTION_TYPES.LIKE
        }
      });
    } else {
      setLikes(likes - 1);

      sendRudderstackEvent(ANALYTICS_EVENTS.SOCIAL_POST_INTERACTION_BY_USER, {
        interaction_type: ANALYTICS_INTERACTION_TYPES.UNLIKE,
        ...analytics
      });

      await removeUserReaction();
    }

    await userReactionsData.refetch();
    onLike();
  }

  function handleLikeClick(e) {
    e.stopPropagation();
    e.preventDefault();

    if (!isLoggedIn) {
      dispatch(
        pushParams(
          {
            direct_signup: SIGNUP_FORM_TYPES.SIGNUP,
            [SIGNUP_REF_ACTION]: SIGNUP_REF_ACTION_TYPES.LIKE_BUTTON,
            [REACTIONABLE_TYPE]: REACTIONABLE_TYPES.BREEDER_POST,
            [REACTIONABLE_ID]: postId
          },
          { hash: true }
        )
      );
    } else {
      updateUserReaction();
    }
  }

  return (
    <button
      type="button"
      className="flex items-center"
      data-test-id={`like-btn-${postId}`}
      disabled={isLoading}
      onClick={handleLikeClick}
    >
      <Icon
        className={classnames('mr1', {
          'rubber-dark-mid': isLiked
        })}
        name={iconName}
      />
      {Boolean(likes) && <p className="font-14 fw-medium">{likes}</p>}
    </button>
  );
}

LikeButton.propTypes = {
  analytics: PropTypes.shape({
    breeder_post_id: PropTypes.number,
    view: PropTypes.string,
    view_feature_location: PropTypes.string
  }),
  numOfLikes: PropTypes.number,
  onLike: PropTypes.func,
  postId: PropTypes.number.isRequired
};
